import { useListGraphWebhooks } from '@squareup/dex-feature-hooks-graph';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  EndpointsWebhookCardList,
  ReferenceDeprecatedIndexPage,
} from '@squareup/dex-tech-ui';
import {
  ReferenceHrefBuilders,
  ReferencePath,
  toUrl,
} from '@squareup/dex-tech-utils-routing';
import { HeadWrapper } from '@squareup/dex-ui';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import {
  filterForDeprecatedGraphObjects,
  addWebhookByApiCategoryToMap,
  getSortedCategoriesWebhooksMap,
} from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import React, { ReactNode, useMemo, useRef, useCallback } from 'react';

import { SquareVersionPage } from '../../../../shared/SquareVersionPage';

const PageContent = () => {
  const { webhooks } = useListGraphWebhooks();
  const filteredWebhooks = filterForDeprecatedGraphObjects(webhooks);
  const webhookListRef = useRef<HTMLElement | null>(null);
  const query = useParams();

  const [sortedCategoriesWebhooksMap] = useMemo(() => {
    if (!filteredWebhooks) {
      return [];
    }

    const categoriesWebhooksMap = filteredWebhooks.reduce(
      addWebhookByApiCategoryToMap,
      new Map()
    );

    const sortedCategoriesWebhooksMap = getSortedCategoriesWebhooksMap(
      categoriesWebhooksMap
    );

    return [sortedCategoriesWebhooksMap];
  }, [filteredWebhooks]);

  const setWebhookListRef = useCallback((ref: HTMLElement | null) => {
    webhookListRef.current = ref;
  }, []);

  const aside = useMemo(() => {
    return {
      trackingId: 'square-version-deprecated-webhooks',
      content: sortedCategoriesWebhooksMap ? (
        <EndpointsWebhookCardList
          categoriesWebhooksMap={sortedCategoriesWebhooksMap}
          setListRef={setWebhookListRef}
        />
      ) : null,
      title: <SpecVersionPickerContainer variant="reference" />,
      navigationItems: [
        {
          trackingId: 'navigate-to-reference-deprecated',
          href: {
            pathname: ReferencePath.VERSION_DEPRECATED,
            query,
          },
          isActive: false,
          testId: 'endpoints',
          text: 'Endpoints',
        },
        {
          trackingId: 'navigate-to-reference-deprecated-webhooks',
          href: {
            pathname: ReferencePath.VERSION_DEPRECATED_WEBHOOKS,
            query,
          },
          isActive: true,
          testId: 'webhooks',
          text: 'Webhooks',
        },
      ],
    };
  }, [setWebhookListRef, sortedCategoriesWebhooksMap, query]);

  const description =
    'View older webhooks that have been deprecated and replaced with newer versions in the Square API.';
  const title = `Deprecated Webhooks - ${getNextPublicApiReferenceNameOrDefault()}`;

  return (
    <>
      <HeadWrapper
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={toUrl(
          ReferenceHrefBuilders.versionDeprecatedWebhooks({
            square_version: query.square_version as string,
          })
        )}
      />
      <ReferenceDeprecatedIndexPage
        aside={aside}
      ></ReferenceDeprecatedIndexPage>
    </>
  );
};

class Page extends SquareVersionPage {
  public static override routeHandlerName =
    '[square_version]/deprecated/webhooks';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default Page;
